import type { TerrainDefinition } from "../terrain/types.ts";
import { packOf } from "./packs.ts";

/**
 * How the editor names a terrain entry, one colour of one wang set (ADR-038 and its amendment of
 * 2026-09-26): the author's name for it, else the pack's rename table, else generic rules over the pack's set
 * and colour names. Pure and game- and pack-agnostic; a pack's own words arrive as data in its table.
 */

/**
 * A pack's rename table: `words` mends whole words of the pack's names before the generic rules (a typo such
 * as `othe`), and `names` replaces an entry outright, keyed by its original name (`terrainOriginalName`).
 */
export interface TerrainNameTable {
  readonly words?: { readonly [word: string]: string };
  readonly names?: { readonly [original: string]: string };
}

/**
 * A pack as the namer needs it: the prefix of its tileset ids, its display name and its rename table.
 */
export interface TerrainNamePack {
  readonly id: string;
  readonly name: string;
  readonly terrainNames?: TerrainNameTable;
}

/**
 * Names the entries of one world: `name` is the display name, `original` the pack's own name for hover.
 */
export interface TerrainNamer {
  readonly name: (terrainId: string, colour: number) => string;
  readonly original: (terrainId: string, colour: number) => string;
}

/**
 * The generic vocabulary, applied in order to lower-cased names: common pack phrasings read the way a painter
 * says them, and transparency marks dropped since the tileset's heading already calls those sets overlays.
 */
const TERRAIN_VOCABULARY: readonly (readonly [RegExp, string])[] = [
  [/\bextra shade grass\b/g, "shaded grass"],
  [/\bmain grass\b/g, "grass"],
  [/\s*\(transparency\)/g, ""],
  [/\s*-\s*transp(?:arent|arency)?$/g, ""],
];

/**
 * The key of an entry in the document's `terrainNames`.
 *
 * @param terrainId Terrain id.
 * @param colour Wang colour index.
 * @returns `<terrain id>#<colour>`.
 */
export function terrainEntryKey(terrainId: string, colour: number): string {
  return `${terrainId}#${colour}`;
}

/**
 * The pack's own name for an entry: the set's name, followed by ` / ` and the colour's name when the set has
 * several colours and the colour is named. It keys the rename table's `names` and shows on hover.
 *
 * @param setName Wang set name.
 * @param colourName Colour name, possibly empty.
 * @param colourCount How many colours the set has.
 * @returns The original name.
 */
export function terrainOriginalName(setName: string, colourName: string, colourCount: number): string {
  return colourCount > 1 && colourName !== "" ? `${setName} / ${colourName}` : setName;
}

/**
 * Escapes text for use inside a regular expression.
 *
 * @param text Literal text.
 * @returns The escaped pattern.
 */
function escapePattern(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Reads the first ` to ` outside brackets as ` on ` (ADR-038 decision 2): the painted region is named first.
 *
 * @param text A lower-cased name.
 * @returns The name with that one word read.
 */
function toAsOn(text: string): string {
  let depth = 0;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === "(") {
      depth += 1;
    } else if (char === ")") {
      depth = Math.max(0, depth - 1);
    } else if (depth === 0 && text.startsWith(" to ", i)) {
      return `${text.slice(0, i)} on ${text.slice(i + 4)}`;
    }
  }
  return text;
}

/**
 * One pack name through the generic rules: the table's word mends, camel-case joins split, lower case, the
 * vocabulary, letters and digits spaced apart, `to` read as `on`, ` - ` read as `, ` and whitespace collapsed.
 *
 * @param raw A set or colour name as the pack writes it.
 * @param words The pack's word mends.
 * @returns The plain name, lower case.
 */
function plainName(raw: string, words: TerrainNameTable["words"]): string {
  let text = raw;
  for (const [word, mended] of Object.entries(words ?? {})) {
    text = text.replace(new RegExp(`\\b${escapePattern(word)}\\b`, "gi"), mended);
  }
  text = text.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  for (const [pattern, replacement] of TERRAIN_VOCABULARY) {
    text = text.replace(pattern, replacement);
  }
  text = text
    .replace(/([a-z])-(\d)/g, "$1 $2")
    .replace(/([a-z])(\d)/g, "$1 $2")
    .replace(/(\d)([a-z])/g, "$1 $2");
  return toAsOn(text)
    .replace(/\s+-\s+/g, ", ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Whether a name holds a phrase as whole words.
 *
 * @param text The name.
 * @param phrase The phrase.
 * @returns True when the phrase appears bounded by non-alphanumerics or the ends.
 */
function holdsWords(text: string, phrase: string): boolean {
  return phrase !== "" && new RegExp(`(^|[^a-z0-9])${escapePattern(phrase)}([^a-z0-9]|$)`).test(text);
}

/**
 * The display name of a terrain entry (ADR-038 amendment decision 1): the author's name when given, else the
 * pack table's name for the entry, else the generic rules. Under the generic rules a multi-colour set names
 * the colour alone when the colour holds the set's name (the repeated set prefix dropped), `<set> <colour>`
 * when the colour is a number and `<set> (<colour>)` otherwise; the result is in sentence case.
 *
 * @example terrainDisplayName("dirt", "dirt1 to main grass", 5) returns "Dirt 1 on grass".
 * @param setName Wang set name.
 * @param colourName Colour name, possibly empty.
 * @param colourCount How many colours the set has.
 * @param table The pack's rename table, if it has one.
 * @param authorName The author's name for the entry, if any; a blank one is none.
 * @returns The display name.
 */
export function terrainDisplayName(setName: string, colourName: string, colourCount: number, table?: TerrainNameTable, authorName?: string): string {
  const own = authorName?.trim() ?? "";
  if (own !== "") {
    return own;
  }
  const renamed = table?.names?.[terrainOriginalName(setName, colourName, colourCount)];
  if (renamed !== undefined) {
    return renamed;
  }
  const set = plainName(setName, table?.words);
  const colour = colourCount > 1 ? plainName(colourName, table?.words) : "";
  const name = colour === "" ? set : holdsWords(colour, set) ? colour : /^\d+$/.test(colour) ? `${set} ${colour}` : `${set} (${colour})`;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * The namer of one world: its terrains, its packs with their tables and the document's names for entries. A
 * tileset id with no pack prefix is its own pack. A terrain the context lacks is named by its id's set part.
 *
 * @param terrains The compile context's terrains.
 * @param packs The world's packs, from its editor catalogue.
 * @param authorNames The document's `terrainNames`.
 * @returns The namer.
 */
export function terrainNamer(terrains: readonly TerrainDefinition[], packs: readonly TerrainNamePack[], authorNames: { readonly [entry: string]: string } | undefined): TerrainNamer {
  const byId = new Map(terrains.map((t) => [t.id, t]));
  const packFor = (terrain: TerrainDefinition): { readonly id: string; readonly pack: TerrainNamePack | undefined } => {
    const id = packOf(terrain.tileset) ?? terrain.tileset;
    return { id, pack: packs.find((p) => p.id === id) };
  };
  const colourName = (terrain: TerrainDefinition, colour: number): string => terrain.colors.find((c) => c.index === colour)?.name ?? "";
  return {
    name: (terrainId, colour) => {
      const terrain = byId.get(terrainId);
      if (terrain === undefined) {
        const slash = terrainId.indexOf("/");
        return slash < 0 ? terrainId : terrainId.slice(slash + 1);
      }
      return terrainDisplayName(terrain.name, colourName(terrain, colour), terrain.colors.length, packFor(terrain).pack?.terrainNames, authorNames?.[terrainEntryKey(terrainId, colour)]);
    },
    original: (terrainId, colour) => {
      const terrain = byId.get(terrainId);
      if (terrain === undefined) {
        return terrainId;
      }
      const { id, pack } = packFor(terrain);
      return `${pack?.name ?? id} · ${terrainOriginalName(terrain.name, colourName(terrain, colour), terrain.colors.length)}`;
    },
  };
}
